/**
 * The NPC markers (sprint-assembly-1, AD-647; meaning set by the owner 2026-10-28,
 * q-quest-marker-glyph-semantics): a floating "?" over an NPC with something for you (a quest
 * to take, lore to ask about, goods to trade) and a "!" over the NPC a ready quest is handed in
 * to, so a new player can find both in a yard of look-alike figures. Built from two primitives (a tapered bar and a dot), warm gold and
 * self-lit so it reads against any ground palette and at dusk fog; it keeps clear of
 * the Bible §3.7 reserved combat hues (red telegraph, green heal).
 *
 * Presentation only; injected into `NpcPlacer` as `createMarker` so the placer stays
 * node-safe. Every marker shares ONE geometry pair and ONE material, so a crowd of
 * questgivers costs two geometries in total. The parts are registered as shared
 * (assets/gpuOwnership.ts, H-1349), so `disposeObject3D` on a cleared NPC root leaves
 * them alone — it used to free them, forcing a re-upload on the next marker's draw.
 */

import * as THREE from 'three';

import { markSharedResource } from '../assets/gpuOwnership.js';

import type { NpcMarkerKind } from '@great-cloud/shared/client';

/**
 * The glyph each marker kind draws. The ONE declaration of what a player sees over an NPC: the
 * shape below is built from it, and social copy is checked against it (the literal is read by
 * tools/social-media/media/subjects.mjs `gameMarkerGlyphs`), so a post naming a glyph the game
 * does not draw is refused and one naming a glyph it does draw is never refused. The SHAPE comes
 * from the glyph; the emphasis (colour, scale) from the kind — a ready turn-in is the brighter,
 * larger one whichever glyph it draws (AD-867).
 */
export const NPC_MARKER_GLYPHS: Readonly<Record<NpcMarkerKind, '!' | '?'>> = {
  offer: '?',
  turnIn: '!',
};

/** The glyph an NPC wears as text (the HUD talk prompt), or "" for none: the same declaration. */
export function markerPrefix(kind: NpcMarkerKind | null): string {
  return kind === null ? '' : `${NPC_MARKER_GLYPHS[kind]} `;
}

/** Marker gold (sRGB) for "something for you": warm, high-key, far from the reserved red/green. */
export const QUEST_MARKER_COLOUR = '#f2c14e';

let shared: {
  bar: THREE.BufferGeometry;
  dot: THREE.BufferGeometry;
  material: THREE.Material;
} | null = null;

function sharedParts(): {
  bar: THREE.BufferGeometry;
  dot: THREE.BufferGeometry;
  material: THREE.Material;
} {
  if (shared === null) {
    shared = {
      bar: new THREE.CylinderGeometry(0.09, 0.05, 0.42, 10),
      dot: new THREE.SphereGeometry(0.075, 12, 8),
      material: new THREE.MeshStandardMaterial({
        color: QUEST_MARKER_COLOUR,
        emissive: QUEST_MARKER_COLOUR,
        emissiveIntensity: 0.9,
        roughness: 0.4,
        metalness: 0.1,
      }),
    };
    markSharedResource(shared.bar, shared.dot, shared.material);
  }
  return shared;
}

/** The turn-in marker (AD-867): brighter than the "?" so a ready quest reads across a yard. */
export const TURN_IN_MARKER_COLOUR = '#ffd75e';

let turnInMaterial: THREE.Material | null = null;

/** The turn-in emphasis material: the brighter gold, more self-lit. */
function turnInGlow(): THREE.Material {
  if (turnInMaterial === null) {
    turnInMaterial = new THREE.MeshStandardMaterial({
      color: TURN_IN_MARKER_COLOUR,
      emissive: TURN_IN_MARKER_COLOUR,
      emissiveIntensity: 1.3,
      roughness: 0.35,
      metalness: 0.1,
    });
    markSharedResource(turnInMaterial);
  }
  return turnInMaterial;
}

let hookShared: { hook: THREE.BufferGeometry; stem: THREE.BufferGeometry } | null = null;

function hookParts(): NonNullable<typeof hookShared> {
  if (hookShared === null) {
    hookShared = {
      // The "?" hook: three quarters of a ring, open at the lower left, its tail meeting the stem.
      hook: new THREE.TorusGeometry(0.15, 0.05, 8, 20, Math.PI * 1.5),
      stem: new THREE.CylinderGeometry(0.05, 0.05, 0.14, 8),
    };
    markSharedResource(hookShared.hook, hookShared.stem);
  }
  return hookShared;
}

/** The marker group's name per kind (tests, the DEV dump, lane probes). */
export const NPC_MARKER_NAMES: Readonly<Record<NpcMarkerKind, string>> = {
  offer: 'npc-marker-offer',
  turnIn: 'npc-marker-turn-in',
};

/**
 * Build one marker for `kind`. Its origin is the marker's BASE (the dot). The shape is the
 * kind's glyph; a turn-in wears the brighter gold at 1.25×. A "?" is not symmetric, so it is
 * flagged `userData.facesCamera` and `NpcPlacer.faceMarkers` turns it to the camera.
 */
export function createNpcMarker(kind: NpcMarkerKind): THREE.Object3D | null {
  const glyph = NPC_MARKER_GLYPHS[kind];
  if (glyph !== '?' && glyph !== '!') return null;
  const parts = sharedParts();
  const material = kind === 'turnIn' ? turnInGlow() : parts.material;
  const group = glyph === '?' ? questionMark(parts.dot, material) : exclamation(parts, material);
  group.name = NPC_MARKER_NAMES[kind];
  group.userData['facesCamera'] = glyph === '?';
  if (kind === 'turnIn') group.scale.setScalar(1.25);
  return group;
}

/** A "!": a dot and a tapered bar. */
function exclamation(
  parts: { bar: THREE.BufferGeometry; dot: THREE.BufferGeometry },
  material: THREE.Material,
): THREE.Group {
  const group = new THREE.Group();
  const dot = new THREE.Mesh(parts.dot, material);
  dot.position.y = 0.075;
  const bar = new THREE.Mesh(parts.bar, material);
  bar.position.y = 0.075 * 2 + 0.08 + 0.21;
  group.add(dot, bar);
  return group;
}

/** A "?": a hook, a stem and the shared-height dot. */
function questionMark(dotGeometry: THREE.BufferGeometry, material: THREE.Material): THREE.Group {
  const parts = hookParts();
  const group = new THREE.Group();
  const dot = new THREE.Mesh(dotGeometry, material);
  dot.position.y = 0.075;
  const stem = new THREE.Mesh(parts.stem, material);
  stem.position.y = 0.075 * 2 + 0.08 + 0.07;
  const hook = new THREE.Mesh(parts.hook, material);
  // Arc from the stem's top round over the crown and down the left: rotate the open quarter to
  // the lower left so the tail sits on the stem.
  hook.rotation.z = -Math.PI / 2;
  hook.position.set(0, stem.position.y + 0.07 + 0.15, 0);
  group.add(dot, stem, hook);
  return group;
}
